/**
 * Long-horizon local reading counters for the v3 identity surfaces (archetype, signal diet).
 * Deduped by card id (a revisit never double-counts), bounded, and best-effort: storage
 * failures are silently ignored, the UI just sees smaller numbers.
 * House style: no em dashes or en dashes anywhere in copy.
 */

const READ_KEY = "insnip.readTrack";
const DIVE_KEY = "insnip.diveTrack";
const MAX_IDS = 600;

export interface ReadTrack {
  ids: string[];
  cats: Record<string, number>;
  goodNews: number;
}

function load(): ReadTrack {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (raw) {
      const d = JSON.parse(raw) as Partial<ReadTrack>;
      return {
        ids: Array.isArray(d.ids) ? d.ids : [],
        cats: d.cats && typeof d.cats === "object" ? (d.cats as Record<string, number>) : {},
        goodNews: Number(d.goodNews) || 0,
      };
    }
  } catch {
    /* ignore */
  }
  return { ids: [], cats: {}, goodNews: 0 };
}

function save(t: ReadTrack): void {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify(t));
  } catch {
    /* ignore */
  }
}

/** Record one card read (id-deduped). Category and goodNews accrue on first sight only. */
export function trackCardRead(id: string, category?: string | null, goodNews?: boolean): void {
  if (!id) return;
  const t = load();
  if (t.ids.includes(id)) return;
  t.ids.push(id);
  if (t.ids.length > MAX_IDS) t.ids = t.ids.slice(-MAX_IDS);
  const cat = (category || "").trim();
  if (cat) t.cats[cat] = (t.cats[cat] ?? 0) + 1;
  if (goodNews) t.goodNews += 1;
  save(t);
}

/** Long-horizon per-category read counts (the diet and archetype input). */
export function categoryReads(): Record<string, number> {
  return load().cats;
}

/** Total tracked reads and good-news reads. */
export function readTotals(): { reads: number; goodNews: number } {
  const t = load();
  return { reads: t.ids.length, goodNews: t.goodNews };
}

/** Record a deep-dive open (id-deduped); returns the running total. */
export function trackDiveOpened(cardId: string): number {
  try {
    const raw = localStorage.getItem(DIVE_KEY);
    const ids: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    const list = Array.isArray(ids) ? ids : [];
    if (cardId && !list.includes(cardId)) {
      list.push(cardId);
      localStorage.setItem(DIVE_KEY, JSON.stringify(list.slice(-MAX_IDS)));
    }
    return list.length;
  } catch {
    return 0;
  }
}

/** Deep dives opened so far. */
export function divesOpened(): number {
  try {
    const raw = localStorage.getItem(DIVE_KEY);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(ids) ? ids.length : 0;
  } catch {
    return 0;
  }
}
